<script setup>
import { reactive } from "vue";
import { useAsyncData } from "#app";
import { useApiUrl } from "~/composable/useApiUrl.js";

const route = useRoute();
const companyId = route.params.companyId;
const { apiUrl } = useApiUrl();
const { data, error } = await useAsyncData(
  companyId,
  () => ($fetch(apiUrl + "/company/detail/" + companyId)),
);

if (error.value?.statusCode === 429) {
  throw createError({
    statusCode: error.value.statusCode,
    message: error.value.data,
  })
}

const company = data.value?.result || {};
const finance = data.value?.result?.financeData || [];
const financeDates = finance.map(s => s.transactionYear);
const firstData = finance?.at(0) || {};
const currentFinance = reactive({
  operatingProfit: firstData?.operatingProfit || null,
  revenue: firstData?.revenue || null,
  totalAssets: firstData?.totalAssets || null,
  totalLiabilities: firstData?.totalLiabilities || null,
  transactionYear: firstData?.transactionYear || null,
});
const switchFinanceYear = (year) => {
  const data = finance.filter(f => f.transactionYear === year)?.at(0);
  currentFinance.operatingProfit = data.operatingProfit;
  currentFinance.revenue = data.revenue;
  currentFinance.totalAssets = data.totalAssets;
  currentFinance.totalLiabilities = data.totalLiabilities;
  currentFinance.transactionYear = data.transactionYear;
};

function numberToKor(number) {
  if (number === null) return "-";

  let negative = false;
  if (number < 0) {
    number = Math.abs(number);
    negative = true;
  }

  const unitWords = ["", "만", "억", "조", "경"];
  const splitUnit = 10000;
  let resultArray = [];
  let resultString = "";

  for (let i = 0; i < unitWords.length; i++) {
    const unitResult = Math.floor((number % Math.pow(splitUnit, i + 1)) / Math.pow(splitUnit, i));
    if (unitResult > 0) {
      resultArray[i] = unitResult;
    }
  }

  for (let i = 0; i < resultArray.length; i++) {
    if (!resultArray[i]) continue;
    resultString = String(resultArray[i]) + unitWords[i] + resultString;
  }

  return `${negative ? "-" : ""}${resultString}${negative ? "" : "+"}`;
}

const bSttCd = (() => {
  if (company?.bSttCd === "01") {
    return "계속사업자";
  } else if (company?.bSttCd === "02") {
    return "휴업사업자";
  } else if (company?.bSttCd === "03") {
    return "페업사업자";
  } else {
    return "-";
  }
})();

const bSttCdClass = (() => {
  if (company?.bSttCd === "01") {
    return "green";
  } else if (company?.bSttCd === "02") {
    return "yellow";
  } else if (company?.bSttCd === "03") {
    return "red";
  } else {
    return "black";
  }
})();

let establishmentDate = null;
if (company?.establishmentDate) {
  establishmentDate = `${company?.establishmentDate.slice(0,4)}-${company?.establishmentDate.slice(4,6)}-${company?.establishmentDate.slice(6)}`
}

const jsonld = {
  "@context": "https://schema.org",
  "@type": "Organization",
  sameAs: `https://biz.partner-s.co/company/${companyId}`,
  image: "https://biz.partner-s.co/images/og_biz_partners_image.png",
  name: company?.cname,
  address: {
    "@type": "PostalAddress",
    streetAddress: company?.address,
  },
  numberOfEmployees: company?.employees,
  foundingDate: establishmentDate,
}

useHead({
  title: `${company.cname} :: 사업자 간편 정보 조회`,
  meta: [
    {
      name: "description",
      content:
        `${company.cname || ""} 사업자등록 정보 :: 대표자 ${company.ceoName || ""}, ${company?.brno || ""}, 사원수 ${company?.employees || "00"}명, ${(company?.establishmentDate?.slice(0, 4) || "0000") + "년 " + (company?.establishmentDate?.slice(4, 6) || "00") + "월"} 설립, ${company?.address || ""}, ${company?.vldtVlKrnNm || ""}, 법인사업자. 기업정보는 공공 데이터를 수집하여 2차가공하여 서비스하는 것으로 각 기관의 자료 갱신시점에 따라 현재 정보와 상이할 수 있습니다.`,
    },
    {
      property: "og:title",
      content: `${company.cname} :: 사업자 간편 정보 조회`,
    },
    {
      property: "og:description",
      content:
        "기업 금융 자산 통합 관리 & 현금흐름 관리 솔루션 파트너스는 기업 재무 관리를 혁신적으로 변화시킵니다.\n" +
        "파트너스 비즈에서는 사업자번호를 통해 사업자 등록 정보를 확인할 수 있습니다.\n" +
        "손쉽게 거래처 등의 정보를 상호명, 법인등록번호 등으로 찾아보세요.",
    },
  ],
  link: [
    { rel: "canonical", href: `https://biz.partner-s.co/company/${route.params.companyId}` },
  ],
  script: [{
    type: "application/ld+json",
    children: JSON.stringify(jsonld)
  }],
});
</script>

<template>
  <div>
    <SearchBannerWithCompany :cname="company?.cname" :bizType="company?.vldtVlKrnNm" />
    <div class="company_detail_layout">
      <div class="partners_promotion_layout">
        <div>
          <h3>혹시 {{ company.ceoName }} <br class="ceo_name_br" /> 대표님이신가요?</h3>
          <p>사업하면서 이런 고민 한번쯤 해보셨나요?</p>
        </div>
        <ul>
          <li>
            <NuxtImg src="/images/checkmark-circle.png" width="30" height="30" format="webp" />
            <div>우리 회사의 기업가치, 무료로 계산해보세요.</div>
          </li>
          <li>
            <NuxtImg src="/images/checkmark-circle.png" width="30" height="30" format="webp" />
            <div>국내 최대 M&A 플랫폼에서 맞춤 매칭, 가치평가, 실사, 자문 연계 등 종합 서비스를 제공합니다.</div>
          </li>
        </ul>
        <div>
          <NuxtImg src="/images/img-ex-1.png" alt="ex_1" width="288px" format="webp"
                   loading="lazy" />
          <NuxtImg src="/images/img-ex-2.png" alt="ex_2" width="288px" format="webp"
                   loading="lazy" />
          <NuxtImg src="/images/img-ex-3.png" alt="ex_3" width="288px" format="webp"
                   loading="lazy" />
        </div>
        <a href="https://mnaexchange.com/?utm_source=partnersBiz&utm_medium=product&utm_campaign=banner">
          <button>무료로 시작하기</button>
        </a>
      </div>
      <div class="company_detail">
        <div class="section_left">
          <div class="detail_head">
            <NuxtImg src="/images/infoIcon.png" quality="80" alt="info_icon" width="32px" height="32px" format="webp" />
            <h4>기본정보</h4>
          </div>
          <div class="detail_content">
            <h4>대표자</h4>
            <p>{{ company?.ceoName || "-" }}</p>
            <h4>사업자번호</h4>
            <p>
              {{ company?.brno ? `${company?.brno.slice(0, 3)}-${company?.brno.slice(3, 5)}-${company?.brno.slice(5)}` : "-"
              }}</p>
            <h4>법인등록번호</h4>
            <p>{{ company?.crno ? `${company?.crno.slice(0, 6)}-${company?.crno.slice(6)}` : "-" }}</p>
            <h4>사원수</h4>
            <p>{{ company?.employees ? company.employees.toLocaleString() + "명" : "-" }}</p>
            <h4>설립일</h4>
            <p>
              {{ company?.establishmentDate ? company?.establishmentDate.slice(0, 4) + "년 " + company?.establishmentDate.slice(4, 6) + "월" : "-"
              }}</p>
            <h4>회사주소</h4>
            <p>{{ company?.address || "-" }}</p>
            <h4>사업자 현재 상태</h4>
            <p :class="`current_biz_state ${bSttCdClass}`">{{ bSttCd || "-" }}</p>
          </div>
        </div>
        <div class="section_right">
          <div class="detail_head">
            <NuxtImg src="/images/listIcon.png" alt="list_icon" width="32px" height="32px" quality="80" format="webp" />
            <div>사업정보</div>
          </div>
          <div class="detail_content_top">
            <div>
              <h4>사업형태</h4>
              <p>{{ company?.vldtVlKrnNm || "-" }}</p>
            </div>
            <div>
              <h4>법인구분</h4>
              <p>{{ company?.wkplStylDvcd || "-" }}</p>
            </div>
            <div>
              <h4>제품/서비스</h4>
              <p>{{ company?.product || "-" }}</p>
            </div>
          </div>
          <div class="detail_head last">
            <NuxtImg src="/images/graphIcon.png" alt="graph_icon" width="32px" height="32px" quality="80" format="webp" />
            <div>재무정보</div>
          </div>
          <div class="detail_content_down">
            <div>
              <ul class="detail_years">
                <li :class="{year_icon: true, blue: date === currentFinance.transactionYear}"
                    @click="switchFinanceYear(date)" v-for="date in financeDates">{{ date }}
                </li>
              </ul>
              <div class="detail_finance">
                <div>
                  <h4>자본금</h4>
                  <p>{{ numberToKor(currentFinance.totalAssets) }}</p>
                </div>
                <div>
                  <h4>매출액</h4>
                  <p>{{ numberToKor(currentFinance.revenue) }}</p>
                </div>
                <div>
                  <h4>영업이익</h4>
                  <p>{{ numberToKor(currentFinance.operatingProfit) }}</p>
                </div>
                <div>
                  <h4>부채</h4>
                  <p>{{ numberToKor(currentFinance.totalLiabilities) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="company_notice">
        <ul>
          <li>
            ※ 기업정보는 국내 금융기관 및 카드사에서 수집한 공공 데이터를 수집하여 2차가공하여 서비스하는 것으로 <br />
            각각의 행정기관의 자료 갱신시점에 따라 현재 정보와 상이할 수 있으며, 자료의 완전성과 정확성을 보장할 수 없으니 단순참고용으로만 활용바랍니다.
          </li>
          <li>
            ※ 사용자 본인판단에 따른 정보이용 결과에 대해 파트너스는 어떠한 책임도 지지 않음을 양지바랍니다.
          </li>
          <li>
            ※ 출처: 국내 금융기관 및 카드사에서 수집한 공공 데이터를 분석하여 제공합니다.
          </li>
        </ul>
      </div>
      <a target="_blank" rel="noopener noreferrer"
         href="https://mna.bridgecode.kr?utm_source=partnersBiz&utm_medium=product&utm_campaign=banner"
         aria-label="브릿지코드에 방문해서 M&N 전문 상담을 받아보세요.">
        <NuxtImg class="banner_pc" src="/images/bridgecode_biz_banner.png" alt="bridgecode_biz_banner"
                 width="960px" height="172px" format="webp" loading="lazy" />
      </a>
      <a target="_blank" rel="noopener noreferrer"
         href="https://mnaexchange.com/?utm_source=partnersBiz&utm_medium=product&utm_campaign=banner"
         aria-label="브릿지코드에 방문해서 M&N 전문 상담을 받아보세요.">
        <NuxtImg class="banner_pc  last" src="/images/img-ex-biz.png" alt="img-ex-biz" width="960px"
                 height="172px" format="webp" loading="lazy" />
      </a>
      <a target="_blank" rel="noopener noreferrer"
         href="https://mna.bridgecode.kr?utm_source=partnersBiz&utm_medium=product&utm_campaign=banner"
         aria-label="브릿지코드에 방문해서 M&N 전문 상담을 받아보세요.">
        <NuxtImg class="banner_mobile" src="/images/mobile_bridgecode_biz_banner.png" alt="bridgecode_biz_banner"
                 width="335px" format="webp" loading="lazy" />
      </a>
      <a target="_blank" rel="noopener noreferrer"
         href="https://mnaexchange.com/?utm_source=partnersBiz&utm_medium=product&utm_campaign=banner"
         aria-label="브릿지코드에 방문해서 M&N 전문 상담을 받아보세요.">
        <NuxtImg class="banner_mobile last" src="/images/img-ex-biz-mobile.png" alt="partners_biz_banner"
                 width="335px" format="webp" loading="lazy" />
      </a>
    </div>
  </div>
</template>

<style scoped lang="scss">
.company_detail_layout {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 60px;

  & > a > img {
    cursor: pointer;
  }
}

.banner_pc.last {
  margin: 32px 0 60px 0;
}

.partners_promotion_layout {
  width: 960px;
  height: 671px;
  padding: 36px;
  background-color: #F8F8F8;
  border: 1px solid #E6E6E6;
  border-radius: 8px;
  box-sizing: border-box;
  margin: 0 0 48px 0;

  & h3 {
    font-size: 28px;
    font-weight: 600;
    margin: 0;
    padding: 0;
  }

  & p {
    font-size: 24px;
    font-weight: 600;
    margin: 16px 0 0 0;
    color: #5D5D5D;
    padding: 0;
  }

  & > div:nth-child(1) {
    padding: 0 0 32px 0;
    border-bottom: 1px solid #DADADA;
    margin: 0 0 32px;
  }

  & > ul:nth-child(2) {
    gap: 11px 0;
    padding: 0;
    margin: 0;

    & > li:first-child {
      margin-bottom: 8px;
    }

    & > li {
      display: flex;
      align-items: center;
      font-size: 20px;
      font-weight: 500;
      padding: 0;
      margin: 0;

      & > img {
        margin: 0 8px 0 0;
      }
    }
  }

  & > div:nth-child(3) {
    display: flex;
    justify-content: space-between;
    margin: 32px 0;
  }

  & > a > button {
    width: 136px;
    height: 45px;
    background-color: #0F0F0F;
    color: #FFF;
    border: none;
    border-radius: 6px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
  }
}

.company_detail {
  display: flex;
  border-top: 1px solid #E6E6E6;
  padding-top: 48px;
}

.section_left {
  width: 468px;
  height: auto;
  margin-right: 24px;

  & > .detail_head {
    display: flex;
    align-items: center;
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 14px;

    & > h4 {
      margin: 0 0 0 8px;
    }
  }

  & > .detail_content {
    padding: 36px 24px;
    background-color: #FAFAFA;
    border-radius: 8px;
    box-sizing: border-box;

    & > h4 {
      color: #B6B6B6;
      font-size: 14px;
      font-weight: 600;
      margin: 0 0 12px 0;
    }

    & > p {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 24px 0;
    }

    & > p:last-child {
      display: flex;
      justify-content: center;
      align-items: center;

      width: 98px;
      height: 31px;
      border-radius: 4px;
      margin: 0;
    }
  }
}

.current_biz_state.green {
  background-color: rgba(0, 227, 0, 0.05);
  color: #2ECC71;
}

.current_biz_state.yellow {
  background-color: rgba(227, 227, 0, 0.05);
  color: #F1C40F;
}

.current_biz_state.red {
  background-color: rgba(227, 0, 0, 0.05);
  color: #F1465B;
}

.current_biz_state.black {
  background-color: rgba(0, 0, 0, 0.05);
  color: #34495E;
}

.section_right {
  width: 468px;
  height: 563px;

  & > .detail_head {
    display: flex;
    align-items: center;
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 16px;

    & > div {
      margin: 0 0 0 8px;
    }
  }

  & > .detail_content_top {
    display: flex;
    flex-flow: wrap;
    width: 468px;
    height: 188px;
    padding: 36px 24px;
    margin-bottom: 43px;
    box-sizing: border-box;
    background-color: #FAFAFA;
    border-radius: 8px;

    & > div {
      width: 209px;
    }

    & > div > h4 {
      color: #B6B6B6;
      font-size: 14px;
      font-weight: 600;
      margin: 0 0 12px 0;
    }

    & > div > p {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 24px 0;
    }
  }

  & > .detail_content_down {
    display: flex;
    flex-flow: wrap;
    width: 468px;
    height: 236px;
    padding: 36px 24px;
    box-sizing: border-box;
    border-radius: 8px;
    background-color: #FAFAFA;

    & > div {
      width: 100%;
    }

    & .detail_finance {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      margin-top: 12px;
    }

    & .detail_finance > div {
      width: 209px;

      & > h4 {
        color: #B6B6B6;
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 12px 0;
        padding: 0;
      }

      & > p {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 24px 0;
        padding: 0;
      }
    }

    & .detail_years {
      display: flex;

      width: 100%;
      padding: 0 0 12px 0;
      margin: 0;

      border-bottom: 1px solid #D3D3D3;
    }

    & .year_icon.blue {
      background-color: #3289FA;
      color: #FFF;
    }

    & .detail_years > li {
      display: flex;
      justify-content: center;
      align-items: center;

      width: 60px;
      height: 24px;
      margin: 0 12px 0 0;
      padding: 0;
      font-size: 12px;
      line-height: 1;

      background-color: #D3D3D3;
      color: #FFF;
      list-style: none;
      border-radius: 3px;

      cursor: pointer;
    }
  }
}

.company_notice {
  width: 960px;
  height: 116px;
  margin: 16px 0 32px 0;

  & > ul {
    padding: 16px 24px;
    margin: 0;
    background-color: #FAFAFA;
  }

  & > ul > li {
    font-size: 12px;
    color: #959595;
    list-style: none;
    line-height: 1.5;
  }

  & > ul > li:nth-child(2) {
    margin: 6px 0;
  }
}

.banner_mobile {
  display: none;
}

.ceo_name_br {
  display: none;
}

@media (max-width: 960px) {
  .company_detail_layout {
    padding-top: 12px;

    & > a > img.last {
      margin: 15px 0 15px 0;
    }
  }

  .partners_promotion_layout {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 335px;
    height: 546px;
    padding: 24px 16.5px;
    background-color: #F8F8F8;
    border: 1px solid #E6E6E6;
    border-radius: 8px;
    box-sizing: border-box;
    margin: 24px 0;

    & .ceo_name_br {
      display: block;
    }

    & h3 {
      font-size: 20px;
      text-align: center;
      line-height: 1.5;
    }

    & p {
      font-size: 16px;
      text-align: center;
      margin: 16px 0 0 0;
    }

    & > div:nth-child(1) {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0 0 24px 0;
      border-bottom: 1px solid #DADADA;
      margin: 0 0 24px;
    }

    & > ul:nth-child(2) {
      width: 245px;
      padding: 0;
      margin: 0;

      & > li:first-child {
        margin-bottom: 8px;
      }

      & > li {
        display: flex;
        align-items: center;
        font-size: 14px;
        text-align: center;
        font-weight: 500;
        line-height: 1.5;

        & > img {
          display: none;
        }
      }
    }

    & > div:nth-child(3) {
      margin: 24px 0;

      & > img:nth-child(1) {
        margin-right: 8px;
      }

      & > img {
        width: 147px;
        height: 146px;
      }

      & > img:nth-child(3) {
        display: none;
      }
    }

    & > a > button {
      width: 136px;
      height: 45px;
      background-color: #0F0F0F;
      color: #FFF;
      border: none;
      border-radius: 6px;
      font-size: 18px;
      font-weight: 600;
    }
  }

  .company_detail {
    max-width: 400px;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    border-top: none;
    padding-top: 0;
  }

  .section_left {
    border-top: 1px solid #E6E6E6;
    padding: 24px 0 0 0;
    margin: 0 24px 24px 24px;
    width: 335px;
  }
  .section_right {
    margin: 0 24px;
    width: 335px;
    height: auto;

    & > .detail_head {
      margin: 0 0 16px 0;
    }

    & > .detail_head.last {
      margin: 20px 0 16px 0;
    }

    & > .detail_content_top {
      width: 335px;
      height: 234px;
      margin: 0;
      padding: 24px 16px;
      flex-wrap: nowrap;
      flex-direction: column;

      & > div {
        width: 100%;
      }

      & > div > h4 {
        margin: 0 0 8px 0;
      }

      & > div > p {
        margin: 0 0 24px 0;
      }
    }

    & > .detail_content_down {
      width: 335px;
      height: 352px;
      padding: 24px 16px;

      .detail_years {
        height: 36px;
        box-sizing: border-box;
      }

      .detail_years > li {
        width: 60px;
        height: 24px;
        font-size: 12px;
      }

      .detail_finance {
        margin: 12px 0 0 0;
      }

      .detail_finance > div {
        height: 70px;
      }

      .detail_finance h4 {
        margin: 0 0 8px 0;
      }

      .detail_finance > div > p {
        margin: 0 0 24px 0;
      }
    }
  }

  .company_notice {
    width: 335px;
    height: auto;
    margin: 16px 0;
  }

  .banner_mobile {
    display: block;
  }

  .banner_pc {
    display: none;
  }
}

</style>